import React, { useState, useEffect } from "react";

const countries = [
  "Philippines",
  "Singapore",
  "Malaysia",
  "Indonesia",
  "Thailand",
  "Vietnam",
  "Japan",
  "South Korea",
  "United States",
  "United Kingdom",
];

function formatDateForInput(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
  return d.toISOString().slice(0, 10);
}

function isExternalUrl(value) {
  return value && /^https?:\/\//i.test(String(value));
}

const EditCoolActivity = ({ event, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    event_name: "",
    description: "",
    event_date: "",
    location: "",
    country: "",
    organizer: "",
    link: "",
  });
  const [media, setMedia] = useState({ file: null, type: "", preview: "" });
  const [mediaLink, setMediaLink] = useState("");
  const [mediaLinkType, setMediaLinkType] = useState("video");
  const [thumbnail, setThumbnail] = useState({ file: null, preview: "" });
  const [removeMedia, setRemoveMedia] = useState(false);
  const [removeThumbnail, setRemoveThumbnail] = useState(false);
  const [mediaChanged, setMediaChanged] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!event) return;

    setForm({
      event_name: event.event_name || "",
      description: event.description || "",
      event_date: formatDateForInput(event.event_date),
      location: event.location || "",
      country: event.country || "",
      organizer: event.organizer || "",
      link: event.link || "",
    });

    setRemoveMedia(false);
    setRemoveThumbnail(false);
    setMediaChanged(false);

    if (event.media) {
      if (isExternalUrl(event.media)) {
        setMediaLink(event.media);
        setMediaLinkType(event.media_type === "image" ? "image" : "video");
        setMedia({ file: null, type: "", preview: "" });
      } else {
        setMediaLink("");
        setMedia({
          file: null,
          type: event.media_type || "",
          preview: event.media_url || "",
        });
      }
    } else {
      setMediaLink("");
      setMedia({ file: null, type: "", preview: "" });
    }

    setThumbnail({
      file: null,
      preview: event.thumbnail_url || "",
    });
  }, [event]);

  const isVideoSelected =
    media.type === "video" ||
    (!media.file && !media.preview && !!mediaLink.trim() && mediaLinkType === "video") ||
    (media.preview && event?.media_type === "video" && !removeMedia && !mediaChanged);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const type = file.type.startsWith("image")
      ? "image"
      : file.type.startsWith("video")
      ? "video"
      : null;
    if (!type) return alert("Only images and videos are allowed");
    setMediaLink("");
    setRemoveMedia(false);
    setMediaChanged(true);
    const reader = new FileReader();
    reader.onloadend = () => setMedia({ file, type, preview: reader.result });
    reader.readAsDataURL(file);
  };

  const removeMediaSelection = () => {
    setMedia({ file: null, type: "", preview: "" });
    setMediaLink("");
    setThumbnail({ file: null, preview: "" });
    setRemoveMedia(true);
    setMediaChanged(true);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image")) {
      alert("Thumbnail must be an image (JPG, PNG, etc.)");
      return;
    }
    setRemoveThumbnail(false);
    const reader = new FileReader();
    reader.onloadend = () =>
      setThumbnail({ file, preview: reader.result });
    reader.readAsDataURL(file);
  };

  const clearThumbnail = () => {
    setThumbnail({ file: null, preview: "" });
    setRemoveThumbnail(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("id", event.id);
    for (const key in form) formData.append(key, form[key]);

    if (removeMedia) {
      formData.append("remove_media", "1");
    } else if (media.file) {
      formData.append("media", media.file);
    } else if (mediaLink.trim()) {
      formData.append("media_link", mediaLink.trim());
      formData.append("media_link_type", mediaLinkType);
    }

    if (removeThumbnail) {
      formData.append("remove_thumbnail", "1");
    } else if (thumbnail.file) {
      formData.append("thumbnail", thumbnail.file);
    }

    const res = await fetch(`${API_URL}/update_cool_activity.php`, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    alert(data.message);
    if (data.success) {
      onUpdated?.();
      onClose?.();
    }
  };

  if (!event) return null;

  const showExistingMedia =
    !removeMedia &&
    (media.preview || mediaLink.trim()) &&
    !media.file;

  return (
    <div style={styles.overlay}>
      <div style={styles.formContainer} onClick={(e) => e.stopPropagation()}>
        <button type="button" style={styles.closeButton} onClick={onClose}>
          ✖
        </button>

        <h3 style={styles.title}>Edit Speaker Session</h3>

        <form onSubmit={handleSubmit} style={styles.form}>
          {[
            "event_name",
            "description",
            "event_date",
            "location",
            "organizer",
            "link",
          ].map((field) => (
            <div key={field} style={styles.formGroup}>
              <label style={styles.label}>
                {field.replace("_", " ").toUpperCase()}
              </label>
              {field === "description" ? (
                <textarea
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="Enter event description..."
                  required
                />
              ) : (
                <input
                  type={field === "event_date" ? "date" : "text"}
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  style={styles.input}
                  required={field !== "link"}
                />
              )}
            </div>
          ))}

          <div style={styles.formGroup}>
            <label style={styles.label}>COUNTRY</label>
            <select
              name="country"
              value={form.country}
              onChange={handleChange}
              style={styles.select}
              required
            >
              <option value="">Select a country</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <label style={styles.label}>Media (upload or link)</label>
          <p style={styles.hint}>
            Leave unchanged to keep current media, or upload / paste a new link.
          </p>

          {showExistingMedia && !media.file ? (
            <div style={styles.preview}>
              {media.preview ? (
                media.type === "image" || event.media_type === "image" ? (
                  <img
                    src={media.preview}
                    alt="current"
                    style={styles.mediaPreview}
                  />
                ) : (
                  <video
                    src={media.preview}
                    controls
                    style={styles.mediaPreview}
                  />
                )
              ) : (
                <p style={styles.hint}>Current: external link ({mediaLink})</p>
              )}
              <button
                type="button"
                style={styles.removeButton}
                onClick={removeMediaSelection}
              >
                ✖
              </button>
            </div>
          ) : !media.preview && !media.file ? (
            <input
              type="file"
              accept="image/*,video/*"
              onChange={handleMediaChange}
              disabled={!!mediaLink.trim()}
              style={styles.fileInput}
            />
          ) : media.file || media.preview ? (
            <div style={styles.preview}>
              {media.type === "image" || (media.preview && media.type !== "video") ? (
                <img
                  src={media.preview}
                  alt="preview"
                  style={styles.mediaPreview}
                />
              ) : (
                <video
                  src={media.preview}
                  controls
                  style={styles.mediaPreview}
                />
              )}
              <button
                type="button"
                style={styles.removeButton}
                onClick={removeMediaSelection}
              >
                ✖
              </button>
            </div>
          ) : null}

          <div style={styles.formGroup}>
            <label style={styles.label}>Media URL (optional)</label>
            <input
              type="url"
              value={mediaLink}
              onChange={(e) => {
                setMediaLink(e.target.value);
                if (e.target.value.trim()) {
                  setMedia({ file: null, type: "", preview: "" });
                  setMediaChanged(true);
                  setRemoveMedia(false);
                }
              }}
              disabled={!!media.preview && !!media.file}
              style={styles.input}
              placeholder="https://www.youtube.com/watch?v=…"
            />
            <div style={styles.linkTypeRow}>
              <span style={styles.linkTypeLabel}>Link type:</span>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  checked={mediaLinkType === "video"}
                  onChange={() => setMediaLinkType("video")}
                />{" "}
                Video
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  checked={mediaLinkType === "image"}
                  onChange={() => setMediaLinkType("image")}
                />{" "}
                Image
              </label>
            </div>
          </div>

          {isVideoSelected && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Video Thumbnail (optional)</label>
              {!thumbnail.preview && !thumbnail.file ? (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  style={styles.fileInput}
                />
              ) : (
                <div style={styles.preview}>
                  <img
                    src={thumbnail.preview}
                    alt="thumbnail"
                    style={styles.mediaPreview}
                  />
                  <button
                    type="button"
                    style={styles.removeButton}
                    onClick={clearThumbnail}
                  >
                    ✖
                  </button>
                </div>
              )}
            </div>
          )}

          <button type="submit" style={styles.submitButton}>
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(15, 15, 25, 0.6)",
    backdropFilter: "blur(6px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 10,
    zIndex: 10000,
  },
  formContainer: {
    position: "relative",
    width: "100%",
    maxWidth: 520,
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(240,240,255,0.9))",
    borderRadius: 20,
    padding: "30px 25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    overflowY: "auto",
    maxHeight: "90vh",
    fontFamily: "'Inter', sans-serif",
  },
  closeButton: {
    position: "absolute",
    top: 12,
    right: 15,
    fontSize: 18,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#666",
  },
  title: {
    textAlign: "center",
    background: "linear-gradient(90deg, #007bff, #6f42c1)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 20,
  },
  form: { display: "flex", flexDirection: "column", gap: 12 },
  formGroup: { display: "flex", flexDirection: "column", gap: 6 },
  hint: { margin: 0, fontSize: 12, color: "#555", lineHeight: 1.4 },
  linkTypeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px 16px",
    marginTop: 6,
  },
  linkTypeLabel: { fontSize: 13, color: "#444" },
  radioLabel: { fontSize: 13, color: "#333", cursor: "pointer" },
  label: { fontWeight: 600, fontSize: 14, color: "#333" },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ccc",
    outline: "none",
    fontSize: 14,
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ccc",
    resize: "vertical",
    outline: "none",
    fontSize: 14,
    minHeight: 80,
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ccc",
    fontSize: 14,
    backgroundColor: "#fff",
    boxSizing: "border-box",
  },
  fileInput: {
    border: "1px solid #ccc",
    borderRadius: 10,
    padding: 10,
    fontSize: 14,
  },
  preview: { position: "relative", marginTop: 5 },
  mediaPreview: {
    width: "100%",
    maxHeight: 220,
    objectFit: "cover",
    borderRadius: 12,
  },
  removeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    border: "none",
    cursor: "pointer",
  },
  submitButton: {
    padding: "12px",
    background: "linear-gradient(90deg, #007bff, #6f42c1)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600,
    marginTop: 15,
  },
};

export default EditCoolActivity;
